import React, { useEffect, useState } from "react";
import { Loader2, Briefcase, MapPin, ExternalLink } from "lucide-react";
import api from "../../lib/api";
import DiscoverLayout from "../../components/discover/DiscoverLayout";
import SearchBar from "../../components/discover/SearchBar";
import FilterBar from "../../components/discover/FilterBar";
import Pagination from "../../components/discover/Pagination";
import { motion } from "framer-motion";

export default function HiringCompaniesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [activeFilters, setActiveFilters] = useState({
    workMode: "All",
  });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/discover/startups?limit=100")
      .then((r) => {
        if (alive) {
          // Enrich startups with list of hiring roles
          const raw = r.data.items || [];
          const hiring = raw.map((c, index) => {
            const roleSets = [
              [
                { title: "Senior Software Engineer - Fullstack", mode: "Remote", salary: "₹18L - ₹24L" },
                { title: "Product Manager - Core Products", mode: "Hybrid", salary: "₹15L - ₹20L" }
              ],
              [
                { title: "AI/ML Scientist", mode: "On-site", salary: "₹25L - ₹35L" },
                { title: "Backend Engineer (Node.js/Python)", mode: "Remote", salary: "₹12L - ₹18L" },
                { title: "Frontend Intern", mode: "Remote", salary: "₹30K/mo" }
              ],
              [
                { title: "Growth Marketing Manager", mode: "Remote", salary: "₹10L - ₹15L" },
                { title: "UI/UX Product Designer", mode: "Hybrid", salary: "₹14L - ₹18L" }
              ],
              [
                { title: "Sales Executive - B2B", mode: "On-site", salary: "₹8L - ₹12L" }
              ],
              [
                { title: "DevOps Architect", mode: "Remote", salary: "₹22L - ₹30L" },
                { title: "Data Analyst", mode: "Hybrid", salary: "₹9L - ₹12L" }
              ]
            ];

            return {
              ...c,
              openRoles: roleSets[index % roleSets.length],
            };
          });
          setItems(hiring);
        }
      })
      .catch((e) => console.error("Error loading hiring companies:", e))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  const handleFilterChange = (key, value) => {
    setActiveFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const filters = [
    { key: "workMode", label: "Work Mode", options: ["Remote", "Hybrid", "On-site"] },
  ];

  const filtered = items.filter((item) => {
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      item.name.toLowerCase().includes(q) ||
      item.openRoles.some((role) => role.title.toLowerCase().includes(q));

    const matchesWorkMode =
      activeFilters.workMode === "All" ||
      item.openRoles.some((role) => role.mode === activeFilters.workMode);

    return matchesSearch && matchesWorkMode;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <DiscoverLayout
      title="Hiring Companies"
      subtitle="Find startups actively building their core teams. Explore open engineering, product, and strategy positions in high-growth companies."
    >
      <div className="space-y-6">
        {/* Search & Stats */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="w-full md:w-96">
            <SearchBar
              value={searchQuery}
              onChange={(q) => {
                setSearchQuery(q);
                setCurrentPage(1);
              }}
              placeholder="Search hiring companies by name or roles..."
            />
          </div>
          <span className="text-xs text-gray-500 font-bold">
            Showing {filtered.length} companies hiring
          </span>
        </div>

        {/* Filters */}
        <FilterBar
          filters={filters}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
        />

        {/* Grid List */}
        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
            <p className="text-sm text-gray-500 font-medium">No hiring companies match your search filters.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {paginatedItems.map((c) => (
                <motion.div
                  key={c.slug}
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white rounded-[24px] border border-gray-150 p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition duration-300 group"
                >
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        {c.domain ? (
                          <img
                            src={`https://logo.clearbit.com/${c.domain}`}
                            alt={`${c.name} logo`}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                            className="w-10 h-10 rounded-xl object-contain bg-white border border-gray-100 p-1 shrink-0 shadow-sm"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-xl bg-indigo-50 text-[#7C3AED] font-display font-semibold flex items-center justify-center border border-indigo-100 shrink-0">
                            {c.name[0]}
                          </div>
                        )}
                        <div>
                          <h3 className="font-display font-bold text-gray-900 text-sm leading-snug group-hover:text-[#7C3AED] transition">
                            {c.name}
                          </h3>
                          <p className="text-[10px] text-gray-400 font-medium flex items-center gap-0.5">
                            <MapPin className="w-3.5 h-3.5" /> {c.location || "India"}
                          </p>
                        </div>
                      </div>

                      <span className="text-[9px] font-bold px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-full border border-indigo-100/50">
                        {c.openRoles.length} Open {c.openRoles.length === 1 ? "Role" : "Roles"}
                      </span>
                    </div>

                    {/* Roles List */}
                    <div className="space-y-2.5 pt-2">
                      {c.openRoles
                        .filter((role) => activeFilters.workMode === "All" || role.mode === activeFilters.workMode)
                        .map((role, idx) => (
                          <div key={idx} className="p-3 bg-gray-50/50 border border-gray-100 rounded-xl flex items-center justify-between gap-4">
                            <div>
                              <p className="text-xs font-bold text-gray-800 leading-snug">{role.title}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                                  role.mode === "Remote" ? "bg-emerald-50 text-emerald-600" : role.mode === "Hybrid" ? "bg-indigo-50 text-indigo-600" : "bg-orange-50 text-orange-600"
                                }`}>
                                  {role.mode}
                                </span>
                                <span className="text-[9px] font-medium text-gray-400">{role.salary}</span>
                              </div>
                            </div>
                            <a
                              href={c.website || `https://${c.domain}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] font-bold text-[#7C3AED] hover:underline shrink-0 flex items-center gap-0.5"
                            >
                              Apply <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-[10px] text-gray-400 font-medium">
                      Industry: <span className="font-semibold text-gray-600">{c.industry}</span>
                    </span>
                    <a
                      href={`/discover/company/${c.slug}`}
                      className="text-xs font-bold text-[#7C3AED] hover:underline"
                    >
                      View Profile →
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </DiscoverLayout>
  );
}
